import {
  FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID,
  FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID,
  FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID,
} from '@/components/flows/detail/slot-list/flow-slot-assets-inline-ids';
import { buildInBetweenPlayoutSegmentId } from '@/helpers/flow-control-in-between-playout.helper';
import {
  isFlowControlInBetweenSegmentKind,
  segmentKindToAnchor,
  type FlowControlSegmentKind,
} from '@/helpers/simulation-cue-engine.helper';
import type { CueSheetRundownSection } from '@/helpers/group-cue-sheet-rundown.helper';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { SlotSegmentAnchor } from '@/components/flows/detail/slot-list/slot-right-section-play-icon';
import { resolveFlowSlotIdFromCueRow } from '@/helpers/simulation-cue-playability.helper';

export function resolveRundownSectionSlotId(section: CueSheetRundownSection): string | undefined {
  if (section.slotId) {
    return section.slotId;
  }
  for (const row of section.rows) {
    const slotId = resolveFlowSlotIdFromCueRow(row);
    if (slotId) {
      return slotId;
    }
  }
  return undefined;
}

export function resolveRundownSectionExpandId(section: CueSheetRundownSection): string {
  if (section.segmentKind === 'pre_flow') {
    return FLOW_CONTROL_PRE_FLOW_PLAYOUT_ID;
  }
  if (section.segmentKind === 'flow_intro') {
    return FLOW_SLOT_ASSETS_INLINE_FLOW_START_ID;
  }
  if (section.segmentKind === 'flow_outro') {
    return FLOW_SLOT_ASSETS_INLINE_FLOW_END_ID;
  }
  if (section.segmentKind === 'after_flow') {
    return FLOW_CONTROL_AFTER_FLOW_PLAYOUT_ID;
  }
  if (section.segmentKind === 'flow_transition' || section.segmentKind === 'flow_resume') {
    const slotId = resolveRundownSectionSlotId(section);
    if (!slotId) {
      return section.key;
    }
    return buildInBetweenPlayoutSegmentId(section.segmentKind, slotId);
  }
  return resolveRundownSectionSlotId(section) ?? section.key;
}

export function resolveRundownSectionAnchor(section: CueSheetRundownSection): SlotSegmentAnchor {
  return segmentKindToAnchor(section.segmentKind as FlowControlSegmentKind);
}

/**
 * Session/break DTO for inline media. In-between sections keep `slotId` for play/expand
 * but must not pass the session slot into the media panel — that panel keys off the
 * `__flow_*_playout__` expand id and would otherwise return null.
 */
export function resolveRundownSectionMediaSlot(
  section: CueSheetRundownSection,
  slots: FlowSlotDto[] | undefined,
): FlowSlotDto | null {
  if (isFlowControlInBetweenSegmentKind(section.segmentKind)) {
    return null;
  }
  if (!section.slotId || !slots?.length) {
    return null;
  }
  return slots.find((entry) => entry.id === section.slotId) ?? null;
}
